import { majorInputToMinor } from "@leylion/design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";
import {
  getStoredContext,
  hasSessionContextChanged,
  SESSION_CONTEXT_CHANGED_EVENT
} from "../api/client";
import {
  confirmFirstLoginCurrency,
  createFirstLoginProduct,
  getFirstLoginState
} from "../api/public-signup-client";
import type { FirstLoginState } from "../api/public-signup-types";
import { currencies } from "./currencies";
import type { CurrencyOption } from "./currencies";
import {
  clearImportCheckpoint,
  clearStableOperationKey,
  getImportRowKey,
  getOrCreateImportCheckpoint,
  getStableOperationKey,
  importRowToken,
  isImportRowCompleted,
  markImportRowCompleted
} from "./first-login-operations";
import "./first-login.css";

type Language = "ar" | "en";
type SetupDialog = "product" | "import" | null;

type ImportProductRow = {
  rowNumber: number;
  code: string | null;
  nameAr: string;
  nameEn: string;
  price: string;
};

const copy = {
  ar: {
    badge: "مرحبًا بك في مساحة عملك الجديدة",
    titleBefore: "أهلاً بك في",
    titleAfter: "— لقد اخترت منظومة صُمّمت لتكبر مع عملك.",
    lead: "من أول عملية بيع إلى إدارة الفروع والمخزون والعملاء والتقارير، Leylion يجمع تشغيلك في مكان واحد ويمنحك بداية بسيطة اليوم، مع القوة التي تحتاجها غدًا.",
    trustOne: "منظومة واحدة مترابطة",
    trustTwo: "جاهزة للنمو مع منشأتك",
    trustThree: "إعداد تدريجي بدون تعقيد",
    startWithConfidence: "ابدأ بثقة",
    signature: "اختر العملة، أضف أول منتج، وابدأ تشغيل Leylion بطريقتك.",
    currencyTitle: "اختر العملة الأساسية لمنشأتك",
    currencyLead: "ستُستخدم في الأسعار والمبيعات والتقارير. لا يمكن بدء وردية أو إنشاء طلب قبل اعتمادها.",
    searchPlaceholder: "ابحث بالعملة أو الرمز أو الدولة",
    noResults: "لا توجد عملة مطابقة للبحث.",
    confirm: "تأكيد العملة",
    confirming: "جارٍ الاعتماد…",
    required: "اختر عملة من نتائج البحث أولًا.",
    confirmed: "تم اعتماد العملة الأساسية",
    catalogTitle: "مساحة عملك جاهزة وتبدأ بدون بيانات تجريبية",
    catalogLead: "لا توجد منتجات أو طلبات أو عملاء تجريبيون. أضف منتجاتك أو استورد ملف CSV عندما تكون جاهزًا.",
    addProduct: "إضافة أول منتج",
    importProducts: "استيراد المنتجات",
    later: "سأكمل لاحقًا",
    language: "English",
    loadError: "تعذر تحميل إعداد البداية. أعد المحاولة.",
    retry: "إعادة المحاولة",
    productTitle: "إضافة أول منتج",
    productLead: "أدخل البيانات الأساسية فقط. سينشئ Leylion الفئة والوحدة وقائمة السعر تلقائيًا.",
    code: "رمز المنتج (اختياري)",
    codePlaceholder: "مثال: LATTE",
    nameAr: "اسم المنتج بالعربية",
    nameArPlaceholder: "مثال: قهوة لاتيه",
    nameEn: "اسم المنتج بالإنجليزية",
    nameEnPlaceholder: "Example: Caffè Latte",
    price: "السعر",
    saveProduct: "حفظ المنتج",
    savingProduct: "جارٍ الحفظ…",
    cancel: "إلغاء",
    productRequired: "أدخل اسمي المنتج والسعر الصحيح.",
    productCreated: "تمت إضافة المنتج وأصبح ظاهرًا في نقطة البيع.",
    importTitle: "استيراد المنتجات من CSV",
    importLead: "استخدم الأعمدة: code, nameAr, nameEn, price. يدعم الملف الفاصلة أو الفاصلة المنقوطة أو Tab.",
    importTemplate: "code,nameAr,nameEn,price\nLATTE,قهوة لاتيه,Caffè Latte,18.00",
    chooseFile: "اختيار ملف CSV",
    importNow: "بدء الاستيراد",
    importing: "جارٍ استيراد {current} من {total}…",
    importRequired: "ألصق بيانات CSV أو اختر ملفًا أولًا.",
    importInvalid: "تعذر قراءة CSV. تأكد من الأعمدة والقيم.",
    importCompleted: "اكتمل استيراد {count} منتج بنجاح.",
    importLimit: "يمكن استيراد 200 منتج كحد أقصى في هذه الخطوة.",
    csvText: "بيانات CSV",
    done: "تم"
  },
  en: {
    badge: "Welcome to your new workspace",
    titleBefore: "Welcome to",
    titleAfter: "— you chose a platform designed to grow with your business.",
    lead: "From the first sale to branches, inventory, customers and reporting, Leylion brings your operations together and gives you a simple start today with the power you will need tomorrow.",
    trustOne: "One connected platform",
    trustTwo: "Ready to grow with you",
    trustThree: "Progressive setup, no clutter",
    startWithConfidence: "Start with confidence",
    signature: "Choose your currency, add the first product, and run Leylion your way.",
    currencyTitle: "Choose your business base currency",
    currencyLead: "It will be used for prices, sales and reports. A shift or order cannot start until it is confirmed.",
    searchPlaceholder: "Search currency, code or country",
    noResults: "No currency matches your search.",
    confirm: "Confirm currency",
    confirming: "Confirming…",
    required: "Choose a currency from the search results first.",
    confirmed: "Base currency confirmed",
    catalogTitle: "Your workspace is ready and starts without demo data",
    catalogLead: "There are no demo products, orders or customers. Add your own items or import a CSV file when ready.",
    addProduct: "Add first product",
    importProducts: "Import products",
    later: "I’ll continue later",
    language: "العربية",
    loadError: "The first-login setup could not be loaded. Try again.",
    retry: "Try again",
    productTitle: "Add your first product",
    productLead: "Enter the essentials only. Leylion creates the base category, unit and price-list setup automatically.",
    code: "Product code (optional)",
    codePlaceholder: "Example: LATTE",
    nameAr: "Arabic product name",
    nameArPlaceholder: "مثال: قهوة لاتيه",
    nameEn: "English product name",
    nameEnPlaceholder: "Example: Caffè Latte",
    price: "Price",
    saveProduct: "Save product",
    savingProduct: "Saving…",
    cancel: "Cancel",
    productRequired: "Enter both product names and a valid price.",
    productCreated: "The product was added and is now available in Point of Sale.",
    importTitle: "Import products from CSV",
    importLead: "Use columns: code, nameAr, nameEn, price. Comma, semicolon and Tab delimiters are supported.",
    importTemplate: "code,nameAr,nameEn,price\nLATTE,قهوة لاتيه,Caffè Latte,18.00",
    chooseFile: "Choose CSV file",
    importNow: "Start import",
    importing: "Importing {current} of {total}…",
    importRequired: "Paste CSV data or choose a file first.",
    importInvalid: "CSV could not be read. Check the columns and values.",
    importCompleted: "Successfully imported {count} products.",
    importLimit: "Up to 200 products can be imported in this step.",
    csvText: "CSV data",
    done: "Done"
  }
} as const;

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function errorMessage(error: unknown, language: Language): string {
  if (!(error instanceof Error)) return copy[language].loadError;
  const problem = error as Error & {
    problem?: { title?: string; detail?: string };
  };
  const code = problem.problem?.title ?? error.message;
  if (code === "CURRENCY_LOCKED_AFTER_OPERATIONS" || code === "CURRENCY_ALREADY_CONFIRMED") {
    return language === "ar"
      ? "تم تثبيت العملة الأساسية ولا يمكن تغييرها من إعداد البداية."
      : "The base currency is already fixed and cannot be changed from first-login setup.";
  }
  if (code === "CURRENCY_CONFIGURATION_CONFLICT") {
    return language === "ar"
      ? "توجد قائمة أسعار بعملة مختلفة. راجع إعدادات التسعير."
      : "A price list already uses another currency. Review pricing settings.";
  }
  if (code === "FIRST_LOGIN_CONTEXT_CHANGED") {
    return language === "ar"
      ? "تم تغيير المؤسسة أو الفرع. أعد فتح إعداد البداية للسياق الحالي."
      : "The organization or branch changed. Reopen first-login setup for the current context.";
  }
  if (code === "PRODUCT_CODE_CONFLICT") {
    return language === "ar"
      ? "رمز المنتج مستخدم بالفعل. اختر رمزًا مختلفًا."
      : "That product code is already used. Choose another code.";
  }
  return problem.problem?.detail || error.message || copy[language].loadError;
}

function welcomeStorageKey(tenantId: string): string {
  return `leylion.first-login.premium-welcome-v5:${tenantId}`;
}

function splitDelimitedLine(line: string, delimiter: string): string[] {
  const values: string[] = [];
  let current = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === delimiter && !quoted) {
      values.push(current.trim());
      current = "";
    } else {
      current += character;
    }
  }
  values.push(current.trim());
  return values;
}

function headerKey(value: string): string {
  return value.trim().toLocaleLowerCase().replace(/[\s_-]+/g, "");
}

function parseProductCsv(value: string): ImportProductRow[] {
  const lines = value
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) return [];

  const first = lines[0];
  const delimiter = first.includes("\t")
    ? "\t"
    : first.split(";").length > first.split(",").length
      ? ";"
      : ",";
  const headers = splitDelimitedLine(first, delimiter).map(headerKey);
  const indexOf = (...names: string[]) => headers.findIndex((item) => names.includes(item));
  const codeIndex = indexOf("code", "productcode", "sku");
  const nameArIndex = indexOf("namear", "arabicname", "productnamear");
  const nameEnIndex = indexOf("nameen", "englishname", "productnameen", "name");
  const priceIndex = indexOf("price", "unitprice", "sellingprice");
  if (nameArIndex < 0 || nameEnIndex < 0 || priceIndex < 0) return [];

  return lines.slice(1).map((line, index) => {
    const values = splitDelimitedLine(line, delimiter);
    return {
      rowNumber: index + 2,
      code: codeIndex >= 0 ? values[codeIndex]?.trim() || null : null,
      nameAr: values[nameArIndex]?.trim() ?? "",
      nameEn: values[nameEnIndex]?.trim() ?? "",
      price: values[priceIndex]?.trim() ?? ""
    };
  }).filter((row) => row.nameAr || row.nameEn || row.price || row.code);
}

function replaceTokens(
  template: string,
  values: Record<string, string | number>
): string {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replace(`{${key}}`, String(value)),
    template
  );
}

export function FirstLoginExperience({
  onCurrencyConfirmed
}: {
  onCurrencyConfirmed: () => void;
}) {
  const [contextKey, setContextKey] = useState<string | null>(null);
  const [sessionBlocked, setSessionBlocked] = useState(hasSessionContextChanged);
  const [state, setState] = useState<FirstLoginState | null>(null);
  const [language, setLanguage] = useState<Language>(() => {
    const stored = localStorage.getItem("leylion.language");
    if (stored === "en" || stored === "ar") return stored;
    return document.documentElement.lang.toLowerCase().startsWith("en") ? "en" : "ar";
  });
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<CurrencyOption | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [dialog, setDialog] = useState<SetupDialog>(null);
  const [productCode, setProductCode] = useState("");
  const [productNameAr, setProductNameAr] = useState("");
  const [productNameEn, setProductNameEn] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [productBusy, setProductBusy] = useState(false);
  const [importText, setImportText] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importProgress, setImportProgress] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const comboboxRef = useRef<HTMLDivElement | null>(null);
  const lastContextRef = useRef("");
  const contextGenerationRef = useRef(0);
  const readGenerationRef = useRef(0);
  const text = copy[language];

  const resetTenantBoundState = () => {
    setState(null);
    setVisible(false);
    setLoading(false);
    setLoadError("");
    setQuery("");
    setSelected(null);
    setListOpen(false);
    setHighlightedIndex(0);
    setSaving(false);
    setError("");
    setNotice("");
    setDialog(null);
    setProductCode("");
    setProductNameAr("");
    setProductNameEn("");
    setProductPrice("");
    setProductBusy(false);
    setImportText("");
    setImportBusy(false);
    setImportProgress("");
  };

  useEffect(() => {
    const block = () => {
      contextGenerationRef.current += 1;
      readGenerationRef.current += 1;
      setSessionBlocked(true);
      // Discard local onboarding drafts, preserving durable import checkpoints.
      resetTenantBoundState();
    };
    window.addEventListener(SESSION_CONTEXT_CHANGED_EVENT, block);
    if (hasSessionContextChanged()) block();
    return () => window.removeEventListener(SESSION_CONTEXT_CHANGED_EVENT, block);
  }, []);

  useEffect(() => {
    if (sessionBlocked) return;
    const syncContext = () => {
      if (hasSessionContextChanged()) return;
      const context = getStoredContext();
      const next = context ? `${context.tenantId}:${context.branchId}` : "";
      if (next === lastContextRef.current) return;
      contextGenerationRef.current += 1;
      lastContextRef.current = next;
      resetTenantBoundState();
      setContextKey(next || null);
    };
    syncContext();
    const timer = window.setInterval(syncContext, 250);
    return () => window.clearInterval(timer);
  }, [sessionBlocked]);

  const load = async (
    expectedContextKey = contextKey,
    expectedGeneration = contextGenerationRef.current
  ) => {
    if (!expectedContextKey || hasSessionContextChanged()) return;
    const readGeneration = ++readGenerationRef.current;
    const isCurrent = () => !hasSessionContextChanged()
      && readGenerationRef.current === readGeneration
      && contextGenerationRef.current === expectedGeneration
      && lastContextRef.current === expectedContextKey;
    setLoading(true);
    setLoadError("");
    try {
      const next = await getFirstLoginState();
      if (!isCurrent()) return;
      setState(next);
      if (!next.isSelfServiceFirstLogin) {
        setVisible(false);
        return;
      }
      const dismissed = localStorage.getItem(welcomeStorageKey(next.tenantId)) === "1";
      setVisible(!next.currencyConfirmed || !dismissed);
    } catch (caught) {
      if (isCurrent()) {
        setLoadError(errorMessage(caught, language));
      }
    } finally {
      if (isCurrent()) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!contextKey || sessionBlocked) return;
    const expectedGeneration = contextGenerationRef.current;
    void load(contextKey, expectedGeneration);
    return () => { readGenerationRef.current += 1; };
  }, [contextKey, sessionBlocked]);

  useEffect(() => {
    const open = () => {
      if (hasSessionContextChanged() || !state?.isSelfServiceFirstLogin) return;
      setVisible(true);
      window.setTimeout(() => {
        if (!hasSessionContextChanged()) inputRef.current?.focus();
      }, 60);
    };
    window.addEventListener("leylion:first-login-open", open);
    return () => window.removeEventListener("leylion:first-login-open", open);
  }, [state?.isSelfServiceFirstLogin]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!comboboxRef.current?.contains(event.target as Node)) {
        setListOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const filteredCurrencies = useMemo(() => {
    const term = normalize(query);
    if (!term || selected) return currencies;
    return currencies.filter((currency) =>
      normalize([
        currency.code,
        currency.symbol,
        currency.nameAr,
        currency.nameEn,
        currency.countryAr,
        currency.countryEn
      ].join(" ")).includes(term)
    );
  }, [query, selected]);

  useEffect(() => {
    setHighlightedIndex((value) =>
      Math.min(value, Math.max(filteredCurrencies.length - 1, 0))
    );
  }, [filteredCurrencies.length]);

  const captureMutationContext = () => {
    if (hasSessionContextChanged()) return null;
    const current = getStoredContext();
    if (
      !state ||
      !current ||
      current.tenantId !== state.tenantId ||
      current.branchId !== state.branchId
    ) {
      setError(errorMessage(new Error("FIRST_LOGIN_CONTEXT_CHANGED"), language));
      return null;
    }

    return {
      generation: contextGenerationRef.current,
      contextKey: `${current.tenantId}:${current.branchId}`
    };
  };

  const mutationContextIsCurrent = (
    generation: number,
    expectedContextKey: string
  ): boolean =>
    !hasSessionContextChanged() &&
    contextGenerationRef.current === generation &&
    lastContextRef.current === expectedContextKey;

  const selectCurrency = (currency: CurrencyOption) => {
    setSelected(currency);
    setQuery(`${currency.code} — ${language === "ar" ? currency.nameAr : currency.nameEn}`);
    setListOpen(false);
    setError("");
  };

  const handleCurrencyKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setListOpen(true);
      setHighlightedIndex((value) =>
        Math.min(value + 1, Math.max(filteredCurrencies.length - 1, 0))
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setListOpen(true);
      setHighlightedIndex((value) => Math.max(value - 1, 0));
    } else if (event.key === "Enter" && listOpen && filteredCurrencies.length) {
      event.preventDefault();
      selectCurrency(filteredCurrencies[highlightedIndex] ?? filteredCurrencies[0]);
    } else if (event.key === "Escape") {
      setListOpen(false);
    }
  };

  const confirmCurrency = async () => {
    if (!selected || !state) {
      setError(text.required);
      setListOpen(true);
      inputRef.current?.focus();
      return;
    }
    const captured = captureMutationContext();
    if (!captured) return;

    const operationName = "currency-confirm";
    const operationKey = getStableOperationKey(
      state.tenantId,
      operationName,
      selected.code
    );
    try {
      setSaving(true);
      setError("");
      setNotice("");
      const next = await confirmFirstLoginCurrency(
        { currency: selected.code },
        operationKey
      );
      if (!mutationContextIsCurrent(captured.generation, captured.contextKey)) {
        return;
      }
      clearStableOperationKey(state.tenantId, operationName);
      setState(next);
      onCurrencyConfirmed();
    } catch (caught) {
      if (mutationContextIsCurrent(captured.generation, captured.contextKey)) {
        setError(errorMessage(caught, language));
      }
    } finally {
      if (mutationContextIsCurrent(captured.generation, captured.contextKey)) {
        setSaving(false);
      }
    }
  };

  const refreshAfterCatalogChange = async (
    message: string,
    expectedGeneration: number,
    expectedContextKey: string
  ) => {
    const next = await getFirstLoginState();
    if (!mutationContextIsCurrent(expectedGeneration, expectedContextKey)) {
      return;
    }
    setState(next);
    setNotice(message);
    setDialog(null);
    onCurrencyConfirmed();
  };

  const saveFirstProduct = async () => {
    if (!state) return;
    const captured = captureMutationContext();
    if (!captured) return;
    const unitPriceMinor = majorInputToMinor(productPrice, state.currency);
    if (!productNameAr.trim() || !productNameEn.trim() || unitPriceMinor === null || unitPriceMinor < 0) {
      setError(text.productRequired);
      return;
    }

    const payload = {
      code: productCode.trim() || null,
      nameAr: productNameAr.trim(),
      nameEn: productNameEn.trim(),
      unitPriceMinor
    };
    const operationName = "product-create";
    const operationKey = getStableOperationKey(
      state.tenantId,
      operationName,
      JSON.stringify(payload)
    );

    try {
      setProductBusy(true);
      setError("");
      await createFirstLoginProduct(payload, operationKey);
      if (!mutationContextIsCurrent(captured.generation, captured.contextKey)) {
        return;
      }
      clearStableOperationKey(state.tenantId, operationName);
      setProductCode("");
      setProductNameAr("");
      setProductNameEn("");
      setProductPrice("");
      await refreshAfterCatalogChange(
        text.productCreated,
        captured.generation,
        captured.contextKey
      );
    } catch (caught) {
      if (mutationContextIsCurrent(captured.generation, captured.contextKey)) {
        setError(errorMessage(caught, language));
      }
    } finally {
      if (mutationContextIsCurrent(captured.generation, captured.contextKey)) {
        setProductBusy(false);
      }
    }
  };

  const importProducts = async () => {
    if (!state) return;
    const captured = captureMutationContext();
    if (!captured) return;
    if (!importText.trim()) {
      setError(text.importRequired);
      return;
    }

    const rows = parseProductCsv(importText);
    if (!rows.length) {
      setError(text.importInvalid);
      return;
    }
    if (rows.length > 200) {
      setError(text.importLimit);
      return;
    }

    const canonicalImport = JSON.stringify(rows.map((row) => ({
      code: row.code?.trim().toUpperCase() || null,
      nameAr: row.nameAr.trim(),
      nameEn: row.nameEn.trim(),
      price: row.price.trim()
    })));
    const checkpoint = getOrCreateImportCheckpoint(
      state.tenantId,
      canonicalImport
    );

    try {
      setImportBusy(true);
      setError("");
      setNotice("");
      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const unitPriceMinor = majorInputToMinor(row.price, state.currency);
        if (!row.nameAr || !row.nameEn || unitPriceMinor === null || unitPriceMinor < 0) {
          throw new Error(
            language === "ar"
              ? `بيانات غير صحيحة في الصف ${row.rowNumber}.`
              : `Invalid data on row ${row.rowNumber}.`
          );
        }
        const payload = {
          code: row.code,
          nameAr: row.nameAr,
          nameEn: row.nameEn,
          unitPriceMinor
        };
        const rowToken = importRowToken(
          checkpoint,
          index,
          JSON.stringify(payload)
        );
        if (isImportRowCompleted(checkpoint, rowToken)) {
          continue;
        }

        setImportProgress(replaceTokens(text.importing, {
          current: checkpoint.completedRows.length + 1,
          total: rows.length
        }));
        await createFirstLoginProduct(
          payload,
          getImportRowKey(state.tenantId, checkpoint, rowToken)
        );
        if (!mutationContextIsCurrent(captured.generation, captured.contextKey)) {
          return;
        }
        markImportRowCompleted(state.tenantId, checkpoint, rowToken);
      }

      clearImportCheckpoint(state.tenantId);
      setImportText("");
      setImportProgress("");
      await refreshAfterCatalogChange(
        replaceTokens(text.importCompleted, { count: rows.length }),
        captured.generation,
        captured.contextKey
      );
    } catch (caught) {
      if (mutationContextIsCurrent(captured.generation, captured.contextKey)) {
        try {
          const next = await getFirstLoginState();
          if (mutationContextIsCurrent(captured.generation, captured.contextKey)) {
            setState(next);
            onCurrencyConfirmed();
          }
        } catch {
          // Preserve the original row failure; the next retry refreshes state.
        }
        if (!mutationContextIsCurrent(captured.generation, captured.contextKey)) return;
        const completed = checkpoint.completedRows.length;
        const partial = completed > 0
          ? language === "ar"
            ? `تم حفظ ${completed} من ${rows.length}. أصلح الصف المتوقف ثم أعد المحاولة لاستكمال الباقي دون تكرار.`
            : `${completed} of ${rows.length} were saved. Fix the stopped row and retry to resume without duplicates.`
          : "";
        setError([errorMessage(caught, language), partial].filter(Boolean).join(" "));
        setImportProgress(partial);
      }
    } finally {
      if (mutationContextIsCurrent(captured.generation, captured.contextKey)) {
        setImportBusy(false);
      }
    }
  };

  const readImportFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const captured = captureMutationContext();
    if (!captured) return;
    try {
      const contents = await file.text();
      if (!mutationContextIsCurrent(captured.generation, captured.contextKey)) return;
      setImportText(contents);
      setError("");
    } catch {
      if (mutationContextIsCurrent(captured.generation, captured.contextKey)) {
        setError(text.importInvalid);
      }
    } finally {
      event.target.value = "";
    }
  };

  const dismiss = () => {
    if (!state?.currencyConfirmed) {
      inputRef.current?.focus();
      return;
    }
    localStorage.setItem(welcomeStorageKey(state.tenantId), "1");
    setVisible(false);
    setDialog(null);
  };

  if (sessionBlocked || hasSessionContextChanged()) return null;
  if (!contextKey || (!loading && !state && !loadError)) return null;
  if (state && !state.isSelfServiceFirstLogin) return null;
  if (!visible && !loadError) return null;

  const currencyRequired = Boolean(
    state?.isSelfServiceFirstLogin && !state.currencyConfirmed
  );

  return (
    <div
      className={`first-login-root${currencyRequired ? " is-blocking" : ""}`}
      dir={language === "ar" ? "rtl" : "ltr"}
      data-testid="first-login-experience"
    >
      {currencyRequired ? <div className="first-login-backdrop" /> : null}

      <section className="first-login-panel" aria-live="polite">
        {state?.currencyConfirmed ? (
          <button
            className="first-login-close"
            type="button"
            aria-label={language === "ar" ? "إغلاق" : "Close"}
            onClick={dismiss}
          >
            ×
          </button>
        ) : null}

        <button
          className="first-login-language"
          type="button"
          onClick={() => {
            const next = language === "ar" ? "en" : "ar";
            setLanguage(next);
            localStorage.setItem("leylion.language", next);
            if (selected) {
              setQuery(`${selected.code} — ${next === "ar" ? selected.nameAr : selected.nameEn}`);
            }
          }}
        >
          {text.language}
        </button>

        {loading && !state ? (
          <div className="first-login-loading">
            <span />
            <p>{language === "ar" ? "جارٍ تجهيز مساحة البداية…" : "Preparing your starting workspace…"}</p>
          </div>
        ) : loadError && !state ? (
          <div className="first-login-load-error" role="alert">
            <strong>{text.loadError}</strong>
            <p>{loadError}</p>
            <button type="button" onClick={() => void load()}>{text.retry}</button>
          </div>
        ) : state ? (
          <>
            <div className="first-login-welcome">
              <div className="first-login-copy">
                <span className="first-login-badge"><i />{text.badge}</span>
                <h1>
                  {text.titleBefore} <strong>Leylion</strong> {text.titleAfter}
                </h1>
                <p>{text.lead}</p>
                <div className="first-login-trust">
                  <span><i />{text.trustOne}</span>
                  <span><i />{text.trustTwo}</span>
                  <span><i />{text.trustThree}</span>
                </div>
              </div>

              <aside className="first-login-signature">
                <span className="first-login-mark">L</span>
                <strong>{text.startWithConfidence}</strong>
                <small>{text.signature}</small>
              </aside>
            </div>

            <div className={`first-login-setup${state.currencyConfirmed ? " is-complete" : ""}`}>
              <div className="first-login-setup-copy">
                <strong>{state.currencyConfirmed ? text.confirmed : text.currencyTitle}</strong>
                <p>
                  {state.currencyConfirmed
                    ? `${state.currency} · ${state.onboardingCompletionPercent}%`
                    : text.currencyLead}
                </p>
              </div>

              {!state.currencyConfirmed ? (
                <div className="first-login-currency-actions">
                  <div className="first-login-combobox" ref={comboboxRef}>
                    <input
                      ref={inputRef}
                      type="text"
                      role="combobox"
                      aria-expanded={listOpen}
                      aria-controls="first-login-currency-list"
                      aria-autocomplete="list"
                      value={query}
                      placeholder={text.searchPlaceholder}
                      onFocus={() => setListOpen(true)}
                      onChange={(event) => {
                        setQuery(event.target.value);
                        setSelected(null);
                        setHighlightedIndex(0);
                        setListOpen(true);
                        setError("");
                      }}
                      onKeyDown={handleCurrencyKeyDown}
                    />
                    <span className="first-login-chevron" aria-hidden="true">⌄</span>

                    {listOpen ? (
                      <div
                        className="first-login-currency-list"
                        id="first-login-currency-list"
                        role="listbox"
                      >
                        {filteredCurrencies.length ? filteredCurrencies.map((currency, index) => (
                          <button
                            key={currency.code}
                            type="button"
                            role="option"
                            aria-selected={selected?.code === currency.code}
                            className={index === highlightedIndex ? "is-highlighted" : ""}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            onClick={() => selectCurrency(currency)}
                          >
                            <span className="first-login-currency-code">{currency.code}</span>
                            <span>
                              <strong>{language === "ar" ? currency.nameAr : currency.nameEn}</strong>
                              <small>{language === "ar" ? currency.countryAr : currency.countryEn} · {currency.symbol}</small>
                            </span>
                          </button>
                        )) : (
                          <p className="first-login-no-results">{text.noResults}</p>
                        )}
                      </div>
                    ) : null}
                  </div>

                  <button
                    className="first-login-confirm"
                    type="button"
                    disabled={saving}
                    onClick={() => void confirmCurrency()}
                  >
                    {saving ? text.confirming : text.confirm}
                  </button>
                </div>
              ) : (
                <div className="first-login-zero-data">
                  <div>
                    <strong>
                      {state.productCount === 0
                        ? text.catalogTitle
                        : language === "ar"
                          ? `تمت إضافة ${state.productCount} منتج إلى مساحة العمل`
                          : `${state.productCount} product${state.productCount === 1 ? "" : "s"} added to the workspace`}
                    </strong>
                    <p>
                      {state.productCount === 0
                        ? text.catalogLead
                        : language === "ar"
                          ? "يمكنك إضافة منتج آخر أو استئناف استيراد CSV أو إنهاء إعداد البداية."
                          : "Add another product, resume a CSV import, or finish first-login setup."}
                    </p>
                  </div>
                  <div>
                    <button type="button" onClick={() => { setDialog("product"); setError(""); }}>
                      {state.productCount === 0
                        ? text.addProduct
                        : language === "ar" ? "إضافة منتج آخر" : "Add another product"}
                    </button>
                    <button className="is-secondary" type="button" onClick={() => { setDialog("import"); setError(""); }}>
                      {text.importProducts}
                    </button>
                    <button className="is-link" type="button" onClick={dismiss}>
                      {state.productCount === 0 ? text.later : text.done}
                    </button>
                  </div>
                </div>
              )}

              {notice ? <p className="first-login-notice" role="status">{notice}</p> : null}
              {error && !dialog ? <p className="first-login-error" role="alert">{error}</p> : null}
            </div>
          </>
        ) : null}
      </section>

      {dialog ? (
        <div className="first-login-dialog-backdrop" role="presentation">
          <section className="first-login-dialog" role="dialog" aria-modal="true">
            <header>
              <div>
                <h2>{dialog === "product" ? text.productTitle : text.importTitle}</h2>
                <p>{dialog === "product" ? text.productLead : text.importLead}</p>
              </div>
              <button
                type="button"
                aria-label={text.cancel}
                disabled={productBusy || importBusy}
                onClick={() => { setDialog(null); setError(""); setImportProgress(""); }}
              >×</button>
            </header>

            {dialog === "product" ? (
              <div className="first-login-dialog-body">
                <label>
                  <span>{text.code}</span>
                  <input
                    dir="ltr"
                    value={productCode}
                    placeholder={text.codePlaceholder}
                    maxLength={80}
                    onChange={(event) => setProductCode(event.target.value.toUpperCase())}
                  />
                </label>
                <div className="first-login-dialog-grid">
                  <label>
                    <span>{text.nameAr}</span>
                    <input
                      dir="rtl"
                      value={productNameAr}
                      placeholder={text.nameArPlaceholder}
                      maxLength={180}
                      onChange={(event) => setProductNameAr(event.target.value)}
                    />
                  </label>
                  <label>
                    <span>{text.nameEn}</span>
                    <input
                      dir="ltr"
                      value={productNameEn}
                      placeholder={text.nameEnPlaceholder}
                      maxLength={180}
                      onChange={(event) => setProductNameEn(event.target.value)}
                    />
                  </label>
                </div>
                <label>
                  <span>{text.price} ({state?.currency})</span>
                  <input
                    dir="ltr"
                    inputMode="decimal"
                    value={productPrice}
                    placeholder="0.00"
                    onChange={(event) => setProductPrice(event.target.value)}
                  />
                </label>
              </div>
            ) : (
              <div className="first-login-dialog-body">
                <div className="first-login-import-toolbar">
                  <label className="first-login-file-button">
                    {text.chooseFile}
                    <input type="file" accept=".csv,text/csv,text/plain" onChange={(event) => void readImportFile(event)} />
                  </label>
                  <small>{text.importLimit}</small>
                </div>
                <label>
                  <span>{text.csvText}</span>
                  <textarea
                    dir="ltr"
                    rows={9}
                    value={importText}
                    placeholder={text.importTemplate}
                    onChange={(event) => setImportText(event.target.value)}
                  />
                </label>
                {importProgress ? <div className="first-login-import-progress"><i /><span>{importProgress}</span></div> : null}
              </div>
            )}

            {error ? <p className="first-login-dialog-error" role="alert">{error}</p> : null}
            <footer>
              <button
                className="is-secondary"
                type="button"
                disabled={productBusy || importBusy}
                onClick={() => { setDialog(null); setError(""); setImportProgress(""); }}
              >
                {text.cancel}
              </button>
              <button
                type="button"
                disabled={productBusy || importBusy}
                onClick={() => void (dialog === "product" ? saveFirstProduct() : importProducts())}
              >
                {dialog === "product"
                  ? productBusy ? text.savingProduct : text.saveProduct
                  : importBusy ? importProgress || text.importNow : text.importNow}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
}
